import { Link } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { groupByDay } from '../../lib/dates.js'
import { Icon } from '../icons/Icon.jsx'
import { Menu } from '../ui/Menu.jsx'
import { UserMenu } from '../ui/UserMenu.jsx'
import styles from './Sidebar.module.css'

/**
 * The Support app's left panel. Same layout as the Knowledge Base sidebar
 * (brand, primary action, search, grouped history, user menu);
 * only the content differs: saved tickets instead of past questions.
 */
export function SupportSidebar({ tickets, activeId, onNew, onOpen, onDelete }) {
  const [search, setSearch] = useState('')
  const q = search.trim().toLowerCase()

  const items = useMemo(() => {
    const all = tickets.data ?? []
    return q ? all.filter((t) => `${t.title} ${t.customer}`.toLowerCase().includes(q)) : all
  }, [tickets.data, q])
  const groups = groupByDay(items, (t) => t.updatedAt)

  return (
    <aside className={styles.sidebar}>
      <Link to="/" className={styles.brand} title="Back to DCKAP Hub">
        <span className={styles.mark}>
          <Icon name="logo" size={18} strokeWidth={1.7} />
        </span>
        <span className={styles.brandText}>
          <span className={styles.brandName}>Support</span>
          <span className={styles.brandSub}>DCKAP Hub</span>
        </span>
      </Link>

      <button
        type="button"
        className={`${styles.newQuestion} ${!activeId ? styles.newQuestionActive : ''}`}
        onClick={onNew}
        style={{ font: 'inherit', fontSize: 14, fontWeight: 500, border: 0, width: '100%', cursor: 'pointer' }}
      >
        <span className={styles.newQuestionLabel}>
          <Icon name="plus" size={16} strokeWidth={1.7} />
          New ticket
        </span>
      </button>

      <label className={styles.search}>
        <Icon name="search" size={15} strokeWidth={1.6} />
        <span className="sr-only">Search saved tickets</span>
        <input type="search" placeholder="Search saved tickets" value={search} onChange={(e) => setSearch(e.target.value)} />
      </label>

      <nav aria-label="Saved tickets" className={styles.history}>
        {tickets.isPending ? (
          <div className={styles.group} aria-hidden="true">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className={styles.skeletonItem}>
                <div className="skeleton" style={{ height: 12, width: `${70 - i * 7}%` }} />
                <div className="skeleton" style={{ height: 10, width: '40%' }} />
              </div>
            ))}
          </div>
        ) : tickets.isError ? (
          <p className={styles.empty}>
            Couldn't load your tickets.{' '}
            <button type="button" className={styles.linkButton} onClick={() => tickets.refetch()}>
              Retry
            </button>
          </p>
        ) : items.length === 0 ? (
          <p className={styles.empty}>{q ? 'No tickets match.' : 'Tickets you save will show up here.'}</p>
        ) : (
          groups.map((group) => (
            <div key={group.label} className={styles.group}>
              <div className={`section-label ${styles.groupLabel}`}>{group.label}</div>
              {group.items.map((ticket) => {
                const active = ticket.id === activeId
                return (
                  <div key={ticket.id} className={`${styles.item} ${active ? styles.itemActive : ''}`}>
                    <button
                      type="button"
                      className={styles.itemLink}
                      aria-current={active ? 'true' : undefined}
                      onClick={() => onOpen(ticket)}
                      style={{ background: 'none', border: 0, font: 'inherit', textAlign: 'left', cursor: 'pointer', color: 'inherit' }}
                    >
                      <span className={styles.itemTitle}>{ticket.title}</span>
                    </button>
                    <Menu
                      trigger={(props) => (
                        <button
                          type="button"
                          className={`icon-btn ${styles.itemMenu}`}
                          aria-label={`Options for ${ticket.title}`}
                          {...props}
                        >
                          <Icon name="dots" size={14} />
                        </button>
                      )}
                      items={[{ label: 'Delete', danger: true, onSelect: () => onDelete(ticket) }]}
                    />
                  </div>
                )
              })}
            </div>
          ))
        )}
      </nav>

      <div className={styles.footer}>
        <div className={styles.user}>
          <UserMenu />
        </div>
      </div>
    </aside>
  )
}
